<template>
  <div class="min-h-screen flex">
    <div
      class="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-teal-dark/10 to-teal/10 items-center justify-center px-8"
    >
      <div class="text-center max-w-md">
        <img
          src="@/assets/images/logo.png"
          alt="Educ360"
          class="w-48 h-48 mx-auto mb-6 object-contain"
        />
        <p class="text-gray-600">
          Connectez-vous avec votre numéro WhatsApp et le code reçu par message.
        </p>
      </div>
    </div>

    <div
      class="w-full lg:w-1/2 min-h-screen lg:h-screen lg:overflow-y-auto flex justify-center px-4 py-8 lg:py-10 bg-white"
    >
      <div class="max-w-md w-full">
        <div class="text-center mb-8">
          <img
            src="@/assets/images/logo.png"
            alt="Educ360"
            class="w-20 h-20 lg:w-24 lg:h-24 mx-auto mb-4 object-contain"
          />
        </div>

        <h2 class="text-2xl font-semibold text-gray-800 mb-2 text-center lg:text-left">
          Connexion
        </h2>
        <p class="text-gray-600 mb-6 text-center lg:text-left">
          Entrez votre numéro WhatsApp
        </p>

        <div
          v-if="error"
          class="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
        >
          {{ error }}
        </div>
        <div
          v-if="successMessage"
          class="mb-6 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm"
        >
          {{ successMessage }}
        </div>

        <!-- Step 1: Phone -->
        <form v-if="!otpSent" @submit.prevent="requestOtp" class="space-y-6">
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
              Numéro WhatsApp
            </label>
            <div class="flex gap-2">
              <select
                v-model="phoneIndicatif"
                class="w-20 sm:w-24 px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition bg-white text-sm"
                :title="selectedIndicatif ? '+' + selectedIndicatif.code : ''"
              >
                <option v-for="opt in indicatifs" :key="opt.code" :value="opt.code">{{ opt.flag }} +{{ opt.code }}</option>
              </select>
              <input
                id="phone"
                v-model="phone"
                type="tel"
                required
                placeholder="12 34 56 78"
                class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none transition"
              />
            </div>
          </div>
          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-teal-dark text-white py-3 rounded-lg font-semibold hover:bg-teal transition-colors disabled:opacity-50"
          >
            <span v-if="loading">Envoi du code...</span>
            <span v-else>Recevoir le code par WhatsApp</span>
          </button>
        </form>

        <!-- Step 2: OTP -->
        <form v-else @submit.prevent="verifyOtp" class="space-y-6">
          <p class="text-sm text-gray-600">
            Un code a été envoyé au {{ displayPhone }}. Entrez-le ci-dessous.
          </p>
          <div>
            <label for="otp" class="block text-sm font-medium text-gray-700 mb-2">
              Code à 6 chiffres
            </label>
            <input
              id="otp"
              v-model="otp"
              type="text"
              inputmode="numeric"
              maxlength="6"
              required
              placeholder="123456"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent outline-none text-center text-2xl tracking-widest"
            />
          </div>
          <button
            type="submit"
            :disabled="loading || otp.length !== 6"
            class="w-full bg-teal-dark text-white py-3 rounded-lg font-semibold hover:bg-teal transition-colors disabled:opacity-50"
          >
            <span v-if="loading">Vérification...</span>
            <span v-else>Se connecter</span>
          </button>
          <button
            type="button"
            :disabled="loading || resendCooldown > 0"
            class="w-full text-teal-dark hover:text-teal text-sm disabled:opacity-50"
            @click.prevent="resendCode"
          >
            <span v-if="resendCooldown > 0">Renvoyer le code ({{ resendCooldown }}s)</span>
            <span v-else>Renvoyer le code</span>
          </button>
          <button
            type="button"
            class="w-full text-gray-600 hover:text-teal text-sm mt-1"
            @click.prevent="goBackToPhone"
          >
            Changer de numéro
          </button>
        </form>

        <div class="mt-6 text-center space-y-2">
          <p class="text-sm text-gray-600">
            Après connexion, ajoutez votre école avec le code fourni par l'établissement.
          </p>
          <p class="text-sm text-gray-600">
            Pas encore de compte ?
            <router-link to="/register" class="text-teal-dark font-semibold hover:underline">
              S'inscrire
            </router-link>
          </p>
          <div class="pt-2">
            <router-link
              to="/"
              class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Retour à l'accueil
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Login",
  data() {
    return {
      phoneIndicatif: "229",
      phone: "",
      otp: "",
      otpSent: false,
      loading: false,
      error: "",
      successMessage: "",
      resendCooldown: 0,
      resendCooldownTimer: null,
      indicatifs: [
        { code: "229", flag: "🇧🇯" },
        { code: "226", flag: "🇧🇫" },
        { code: "237", flag: "🇨🇲" },
        { code: "225", flag: "🇨🇮" },
        { code: "242", flag: "🇨🇬" },
        { code: "245", flag: "🇬🇼" },
        { code: "241", flag: "🇬🇦" },
        { code: "223", flag: "🇲🇱" },
        { code: "227", flag: "🇳🇪" },
        { code: "234", flag: "🇳🇬" },
        { code: "221", flag: "🇸🇳" },
        { code: "228", flag: "🇹🇬" },
      ],
    };
  },
  computed: {
    selectedIndicatif() {
      return this.indicatifs.find((i) => i.code === this.phoneIndicatif) || null;
    },
    fullPhone() {
      const p = (this.phone || "").replace(/\D/g, "");
      return p ? `${this.phoneIndicatif}${p}` : "";
    },
    displayPhone() {
      const prefix = "+" + this.phoneIndicatif + " ";
      return prefix + (this.phone || "—");
    },
  },
  methods: {
    goBackToPhone() {
      this.otpSent = false;
      this.otp = "";
      this.successMessage = "";
      this.error = "";
      this.resendCooldown = 0;
      if (this.resendCooldownTimer) {
        clearInterval(this.resendCooldownTimer);
        this.resendCooldownTimer = null;
      }
    },
    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendCooldownTimer) clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0 && this.resendCooldownTimer) {
          clearInterval(this.resendCooldownTimer);
          this.resendCooldownTimer = null;
        }
      }, 1000);
    },
    async resendCode() {
      if (this.resendCooldown > 0 || this.loading) return;
      await this.requestOtp();
      if (this.otpSent) this.startResendCooldown();
    },
    async requestOtp() {
      try {
        this.error = "";
        this.successMessage = "";
        this.loading = true;
        await this.$store.dispatch("userRequestOtp", {
          phone: this.fullPhone || this.phoneIndicatif + this.phone.replace(/\D/g, ""),
          register: false,
        });
        this.otpSent = true;
        this.successMessage = "Code envoyé. Vérifiez votre WhatsApp.";
        this.startResendCooldown();
      } catch (err) {
        this.error = err.response?.data?.error || "Impossible d'envoyer le code. Vérifiez le numéro.";
      } finally {
        this.loading = false;
      }
    },
    async verifyOtp() {
      try {
        this.error = "";
        this.loading = true;
        await this.$store.dispatch("userVerifyOtp", {
          phone: this.fullPhone || this.phoneIndicatif + this.phone.replace(/\D/g, ""),
          otp: this.otp.trim(),
        });
        this.$router.push({ path: "/app" });
      } catch (err) {
        this.error = err.response?.data?.error || "Code invalide ou expiré. Réessayez.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
